import { Box, Grid } from '@mui/material';
import MediumTypography from '../../components/formlib/MediumTypography';
import Textinput from '../../components/formlib/Textinput';
import SingleLine from '../../components/formlib/SingleLine';
import CustomRadioButton, {
  RadioButtonData,
} from '../../components/formlib/CustomRadioButton';
import CommentTextbox from '../../components/formlib/CommentTextbox';
import { useIntl } from 'react-intl';
import { ConsentInsurance } from '../../services/configApi/forms/Consent/ConsentServices';
import DropdownComponent from '../../components/formlib/DropdownComponent';
import { OptionType } from '../../utils/type';

const consentToInsuranceJson: RadioButtonData[] = [
  {
    value: 'authorize',
    titleId: 'accessInsuranceText',
    defaultTitleId: 'Access Insurance',
    labelId: 'insuranceConsent.authorize',
    defaultLabel:
      'I/We authorize the Thom Child and Family Services to bill our insurance company for services provided and to release related records to the insurance company/companies added above.',
  },
  {
    value: 'uninsured',
    titleId: 'uninsuredText',
    defaultTitleId: 'Uninsured',
    labelId: 'insuranceConsent.uninsured',
    defaultLabel:
      'My child is presently uninsured. I/we have received information about programs that I/we may qualify for in order to obtain medical coverage through one of the programs offered by the Division of Medical Assistance.',
  },
  {
    value: 'unauthorize',
    titleId: 'unauthorizedAccessInsuranceFormText',
    defaultTitleId: 'Refusal To Access Insurance Form',
    labelId: 'insuranceConsent.unauthorize',
    defaultLabel:
      'While I recognize that Early Intervention services are typically covered under public and/or private insurance without co-pays or deductibles for such services, I refuse access to my family/child’s insurance to pay for my child’s Early Intervention services.',
  },
];

interface ConsentInsuranceDetailsProps {
  insuranceLookup: OptionType[];
  consentInsurance: ConsentInsurance;
  hasEditPermission: boolean;
  updateConsentInsurance: (consentInsurance: ConsentInsurance) => void;
}

const ConsentInsuranceDetails = ({
  insuranceLookup,
  consentInsurance,
  hasEditPermission,
  updateConsentInsurance,
}: ConsentInsuranceDetailsProps) => {
  const rctl = useIntl();

  return (
    <Box>
      <MediumTypography
        labelid="insuranceInformationText"
        defaultlabel="Insurance Information"
        className="mainText-md mt-md mb-md"
        fontweight={600}
      />

      <Grid
        container
        rowSpacing={'24px'}
        columnSpacing={'40px'}
        key={'consentInsurance'}
      >
        <Grid item xs={4} lg={4}>
          <DropdownComponent
            value={consentInsurance.companyId ?? ''}
            labelid="insuranceNameText"
            defaultlabelid="3rd Party Payer (Insurance Name)"
            names={insuranceLookup}
            handleChange={(value: string) => {
              const selectedInsurance = insuranceLookup.find(
                (item) => item.id === value,
              );
              if (!selectedInsurance) {
                updateConsentInsurance({
                  ...consentInsurance,
                  companyId: '',
                  companyName: '',
                });
              } else if (value === '') {
                updateConsentInsurance({
                  ...consentInsurance,
                  companyId: null,
                  companyName: null,
                });
              } else {
                updateConsentInsurance({
                  ...consentInsurance,
                  companyId: selectedInsurance.id,
                  companyName: selectedInsurance.label,
                });
              }
            }}
            disabled={!hasEditPermission}
            Required={consentInsurance.consentToInsurance === 'authorize'}
          />
        </Grid>
        <Grid item xs={3} lg={3}>
          <Textinput
            labelid="policyNumberText"
            defaultlabelid="Policy #"
            inputProps={{
              maxLength: 50,
            }}
            Value={consentInsurance.policyNumber}
            handlechange={(value: string) => {
              updateConsentInsurance({
                ...consentInsurance,
                policyNumber: value,
              });
            }}
            disabled={!hasEditPermission}
          />
        </Grid>

        <Grid item xs={4} lg={4}>
          <Textinput
            labelid="subscriberNameText"
            defaultlabelid="Subscriber Name"
            Value={consentInsurance.subscriberName}
            inputProps={{
              maxLength: 50,
            }}
            handlechange={(value: string) => {
              updateConsentInsurance({
                ...consentInsurance,
                subscriberName: value,
              });
            }}
            disabled={!hasEditPermission}
          />
        </Grid>

        {consentInsurance.companyId === 'backend-others' && (
          <Grid item xs={4} lg={4}>
            <Textinput
              labelid="otherCompanyNameText"
              defaultlabelid="Other Company Name"
              Value={consentInsurance.otherCompanyName}
              inputProps={{
                maxLength: 50,
              }}
              handlechange={(value: string) => {
                updateConsentInsurance({
                  ...consentInsurance,
                  otherCompanyName: value,
                });
              }}
              Required={consentInsurance.companyId === 'backend-others'}
              disabled={!hasEditPermission}
            />
          </Grid>
        )}
      </Grid>

      <Box>
        <MediumTypography
          labelid="consentText"
          defaultlabel="Consent"
          className="mainText-md mt-lg"
          fontweight={600}
        />

        <CustomRadioButton
          data={consentToInsuranceJson}
          value={consentInsurance.consentToInsurance}
          onSelectItem={(value) => {
            updateConsentInsurance({
              ...consentInsurance,
              consentToInsurance:
                consentInsurance.consentToInsurance === value
                  ? ''
                  : value ?? '',
            });
          }}
          textStyleClassName="mt-sm mb-md"
        />

        <CommentTextbox
          className="mb-md"
          placeholder={rctl.formatMessage({
            id: 'CommunicationLogPage.enterNote',
            defaultMessage: 'Enter Note',
          })}
          name="notes"
          maxLength={1000}
          rows={4}
          Value={consentInsurance.insuranceNotes}
          handlechange={(text) => {
            updateConsentInsurance({
              ...consentInsurance,
              insuranceNotes: text,
            });
          }}
        />
      </Box>

      <SingleLine />
    </Box>
  );
};

export default ConsentInsuranceDetails;
